import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useReveal } from "../lib/useReveal";
import {
    DIRECTORY_IS_SAMPLE_DATA,
    getTypeLabel,
} from "../data/directory";
import { buildSearchFilter, fetchMetadata, searchRehabCenters } from "../lib/rehabDirectoryApi";
import { mapApiItemsToListings } from "../lib/mapRehabListing";
import {
    BadgeCheck,
    MapPin,
    Bed,
    ArrowRight,
    Search,
    ShieldCheck,
    SlidersHorizontal,
    Compass,
    X,
    ChevronLeft,
    ChevronRight,
    Loader2,
    SearchX,
} from "lucide-react";

function DirectoryCardSkeleton() {
    return (
        <article className="bg-card border rounded-2xl overflow-hidden flex flex-col animate-pulse">
            <div className="aspect-[16/10] bg-muted" />
            <div className="p-6 flex flex-col flex-1 gap-3">
                <div className="h-5 w-4/5 rounded bg-muted" />
                <div className="h-3 w-3/5 rounded bg-muted" />
                <div className="space-y-2 pt-1">
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                </div>
                <div className="flex gap-2 pt-2">
                    <div className="h-5 w-16 rounded-full bg-muted" />
                    <div className="h-5 w-20 rounded-full bg-muted" />
                </div>
                <div className="mt-auto pt-4 border-t flex justify-between items-center">
                    <div className="h-3 w-24 rounded bg-muted" />
                    <div className="h-3 w-20 rounded bg-muted" />
                </div>
            </div>
        </article>
    );
}

function DirectoryLoadingGrid({ count = 6 }) {
    return (
        <div
            data-testid="directory-loading-state"
            className="space-y-6"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span>Loading treatment centers...</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: count }, (_, index) => (
                    <DirectoryCardSkeleton key={index} />
                ))}
            </div>
        </div>
    );
}

function DirectoryEmptyState({ title, description, icon: Icon = SearchX, action }) {
    return (
        <div
            data-testid="directory-empty-state"
            className="text-center py-16 px-6 bg-muted/40 rounded-[2rem] border"
        >
            <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                <Icon className="w-6 h-6" />
            </div>
            <p className="text-xl font-bold text-foreground">{title}</p>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">{description}</p>
            {action}
        </div>
    );
}

function DirectoryPagination({ pageNumber, totalPages, totalRecords, isLoading, onPageChange }) {
    if (totalRecords === 0) {
        return null;
    }

    const pages = buildPageNumbers(pageNumber, totalPages);

    return (
        <nav
            aria-label="Directory pagination"
            data-testid="directory-pagination"
            className="mt-10 rounded-[2rem] border bg-card px-4 py-5 shadow-sm"
        >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground text-center sm:text-left">
                    Page{" "}
                    <span className="font-semibold text-foreground">{pageNumber}</span> of{" "}
                    <span className="font-semibold text-foreground">{totalPages}</span>
                    <span className="hidden sm:inline">
                        {" "}
                        · {totalRecords.toLocaleString()} total record{totalRecords === 1 ? "" : "s"}
                    </span>
                </p>

                <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={() => onPageChange(pageNumber - 1)}
                        disabled={pageNumber <= 1 || isLoading}
                        aria-label="Previous page"
                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border bg-background text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                    </button>

                    <div className="flex items-center gap-1">
                        {pages.map((page, index) =>
                            page === "..." ? (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="px-2 text-sm text-muted-foreground"
                                >
                                    …
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    type="button"
                                    onClick={() => onPageChange(page)}
                                    disabled={isLoading || page === pageNumber}
                                    aria-label={`Go to page ${page}`}
                                    aria-current={page === pageNumber ? "page" : undefined}
                                    className={`min-w-10 h-10 px-3 rounded-full text-sm font-semibold transition-colors ${
                                        page === pageNumber
                                            ? "bg-primary text-primary-foreground"
                                            : "border bg-background hover:bg-muted disabled:opacity-50"
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => onPageChange(pageNumber + 1)}
                        disabled={pageNumber >= totalPages || isLoading}
                        aria-label="Next page"
                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border bg-background text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                    >
                        Next
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </nav>
    );
}

function buildPageNumbers(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, index) => index + 1);
    }

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
    const result = [];

    for (let index = 0; index < sorted.length; index += 1) {
        result.push(sorted[index]);
        if (sorted[index + 1] - sorted[index] > 1) {
            result.push("...");
        }
    }

    return result;
}

function ListingImage({ src, alt, className = "" }) {
    const [hasError, setHasError] = useState(false);
    const hasImage = Boolean(src?.trim()) && !hasError;

    useEffect(() => {
        setHasError(false);
    }, [src]);

    if (!hasImage) {
        return (
            <div
                className={`flex items-center justify-center bg-muted text-sm font-medium text-muted-foreground ${className}`}
            >
                No image Preview
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => setHasError(true)}
            className={className}
        />
    );
}

export function DirectoryCard({ listing, onViewDetails, showVerified }) {
    return (
        <article
            data-testid={`directory-card-${listing.id}`}
            className="group bg-card border rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5 transition-all flex flex-col"
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <ListingImage
                    src={listing.image}
                    alt={listing.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {showVerified && listing.verified && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-background/95 backdrop-blur rounded px-2 py-1 text-[11px] font-semibold text-primary border">
                        <BadgeCheck size={12} />
                        Verified
                    </div>
                )}
            </div>
            <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-foreground leading-snug">{listing.name}</h3>
                <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
                    {listing.address && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={12} className="shrink-0" />
                            <span className="line-clamp-1">
                                {listing.address}, {listing.city}, {listing.state}
                            </span>
                        </span>
                    )}
                    {!listing.address && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={12} /> {listing.city}, {listing.state}
                        </span>
                    )}
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                    {listing.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {listing.tags.map((t) => (
                        <span
                            key={t}
                            className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground font-medium"
                        >
                            {t}
                        </span>
                    ))}
                </div>
                <div className="mt-5 pt-4 border-t flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                        {DIRECTORY_IS_SAMPLE_DATA ? "Sample listing" : "Informational only"}
                    </span>
                    <button
                        type="button"
                        onClick={() => onViewDetails(listing)}
                        data-testid={`directory-card-details-${listing.id}`}
                        className="inline-flex items-center gap-1 text-primary text-sm font-semibold group-hover:gap-2 transition-all"
                    >
                        View details <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </article>
    );
}

const DEFAULT_STATE = "California";
const DEFAULT_SERVICE_TYPE = "all";
const ALL_PROGRAMS_OPTION = { key: "all", value: "All Programs" };
const PAGE_SIZE = 6;
const RESULTS_ANCHOR_ID = "directory-results";

function getStateCode(states, stateName) {
    return states.find((item) => item.value === stateName)?.key ?? "";
}

function getCitiesForState(cities, stateCode) {
    if (!stateCode) {
        return [];
    }
    return cities.filter((city) => city.stateCode === stateCode);
}

function LocationFilters({
    state,
    city,
    rehabStates,
    availableCities,
    disabled,
    onStateChange,
    onCityChange,
    onSelectAllCities,
}) {
    const locationLabel = city ? `${city}, ${state}` : `All cities, ${state}`;

    return (
        <div
            data-testid="directory-location-filters"
            className="rounded-2xl border bg-muted/30 p-3 sm:p-4 space-y-3"
        >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 text-primary" aria-hidden />
                    Location
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <p
                        data-testid="directory-location-summary"
                        className="inline-flex items-center gap-1.5 rounded-full bg-background border px-3 py-1 text-xs font-semibold text-foreground"
                    >
                        <MapPin className="w-3 h-3 text-primary shrink-0" aria-hidden />
                        <span className="truncate">{locationLabel}</span>
                    </p>
                    <button
                        type="button"
                        onClick={onSelectAllCities}
                        disabled={disabled || !city}
                        data-testid="directory-location-all-button"
                        className="inline-flex h-8 items-center rounded-full border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        All
                    </button>
                    {city ? (
                        <button
                            type="button"
                            onClick={onSelectAllCities}
                            disabled={disabled}
                            data-testid="directory-clear-filters-button"
                            className="inline-flex h-8 items-center rounded-full border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Clear
                        </button>
                    ) : null}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1.5">
                    <span className="block text-[11px] font-semibold text-muted-foreground">State</span>
                    <select
                        value={state}
                        onChange={(e) => onStateChange(e.target.value)}
                        disabled={disabled}
                        data-testid="directory-state-select"
                        aria-label="Select state"
                        className="w-full h-11 px-4 rounded-xl bg-background border border-transparent focus:border-primary outline-none text-sm text-foreground transition-colors disabled:opacity-60"
                    >
                        {rehabStates.map((stateOption) => (
                            <option key={stateOption.key} value={stateOption.value}>
                                {stateOption.value}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="space-y-1.5">
                    <span className="block text-[11px] font-semibold text-muted-foreground">City</span>
                    <select
                        value={city}
                        onChange={(e) => onCityChange(e.target.value)}
                        disabled={disabled}
                        data-testid="directory-city-select"
                        aria-label={`Select city in ${state}`}
                        className="w-full h-11 px-4 rounded-xl bg-background border border-transparent focus:border-primary outline-none text-sm text-foreground transition-colors disabled:opacity-60"
                    >
                        <option value="">All cities in {state}</option>
                        {availableCities.map((cityOption) => (
                            <option
                                key={`${cityOption.stateCode}-${cityOption.name}`}
                                value={cityOption.name}
                            >
                                {cityOption.name}, {state}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        </div>
    );
}

export default function Directory({ mode = "teaser" }) {
    const requestIdRef = useRef(0);
    const [serviceType, setServiceType] = useState(DEFAULT_SERVICE_TYPE);
    const [state, setState] = useState(DEFAULT_STATE);
    const [city, setCity] = useState("");
    const [rehabServiceTypes, setRehabServiceTypes] = useState([]);
    const [rehabStates, setRehabStates] = useState([]);
    const [rehabCities, setRehabCities] = useState([]);
    const [metadataError, setMetadataError] = useState("");
    const [isMetadataLoading, setIsMetadataLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [detailListing, setDetailListing] = useState(null);
    const [listings, setListings] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const isTeaser = mode === "teaser";
    const activePage = isTeaser ? 1 : pageNumber;
    const selectedStateCode = getStateCode(rehabStates, state);
    const availableCities = getCitiesForState(rehabCities, selectedStateCode);
    const serviceTypeOptions = [ALL_PROGRAMS_OPTION, ...rehabServiceTypes];
    const filtersReady = !isMetadataLoading && rehabStates.length > 0;
    const ref = useReveal([isLoading, listings.length, pageNumber, error, filtersReady]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadMetadata() {
            setIsMetadataLoading(true);
            setMetadataError("");

            try {
                const data = await fetchMetadata({ signal: controller.signal });
                setRehabServiceTypes(data.rehabServiceTypes ?? []);
                setRehabStates(data.rehabStates ?? []);
                setRehabCities(data.rehabCities ?? []);
            } catch (metadataFetchError) {
                if (metadataFetchError.name === "AbortError") {
                    return;
                }
                setMetadataError(
                    metadataFetchError.message || "Unable to load directory filters right now."
                );
            } finally {
                if (!controller.signal.aborted) {
                    setIsMetadataLoading(false);
                }
            }
        }

        loadMetadata();

        return () => controller.abort();
    }, []);

    useEffect(() => {
        setPageNumber(1);
    }, [serviceType, state, city, searchQuery]);

    const handleSearch = useCallback(() => {
        setSearchQuery(query.trim());
        setPageNumber(1);
    }, [query]);

    const fetchListings = useCallback(
        async ({ page = 1, signal } = {}) => {
            if (!filtersReady) {
                setIsLoading(false);
                return;
            }

            setListings([]);
            setIsLoading(true);
            setError("");

            const requestId = ++requestIdRef.current;

            try {
                const data = await searchRehabCenters({
                    pageNumber: page,
                    pageSize: PAGE_SIZE,
                    filter: buildSearchFilter({
                        state,
                        serviceType,
                        city,
                        query: searchQuery,
                    }),
                    signal,
                });

                if (requestId !== requestIdRef.current || signal.aborted) {
                    return;
                }

                const mapped = mapApiItemsToListings(data.items);

                setListings(mapped);
                setTotalRecords(data.totalRecords ?? 0);
                setTotalPages(data.totalPages ?? 0);
            } catch (fetchError) {
                if (fetchError.name === "AbortError") {
                    return;
                }
                if (requestId !== requestIdRef.current) {
                    return;
                }
                setListings([]);
                setTotalRecords(0);
                setTotalPages(0);
                setError(fetchError.message || "Unable to load treatment directory right now.");
            } finally {
                if (requestId === requestIdRef.current && !signal?.aborted) {
                    setIsLoading(false);
                }
            }
        },
        [city, filtersReady, searchQuery, serviceType, state]
    );

    useEffect(() => {
        const controller = new AbortController();
        fetchListings({ page: activePage, signal: controller.signal });

        return () => controller.abort();
    }, [activePage, fetchListings]);

    const handlePageChange = (nextPage) => {
        const safePage = Math.min(Math.max(1, nextPage), Math.max(totalPages, 1));
        if (safePage === pageNumber || isLoading) {
            return;
        }
        setPageNumber(safePage);
        document.getElementById(RESULTS_ANCHOR_ID)?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    useEffect(() => {
        if (!detailListing) {
            return;
        }
        const onKey = (e) => {
            if (e.key === "Escape") {
                setDetailListing(null);
            }
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [detailListing]);

    const handleCityChange = (nextCity) => {
        setCity(nextCity);

        if (!nextCity) {
            return;
        }

        const selectedCity = rehabCities.find(
            (item) =>
                item.name === nextCity &&
                (!selectedStateCode || item.stateCode === selectedStateCode)
        );

        if (!selectedCity) {
            return;
        }

        const matchedState = rehabStates.find((item) => item.key === selectedCity.stateCode);
        if (matchedState) {
            setState(matchedState.value);
        }
    };

    const handleStateChange = (nextState) => {
        setState(nextState);

        if (!city) {
            return;
        }

        const nextStateCode = getStateCode(rehabStates, nextState);
        const selectedCity = rehabCities.find((item) => item.name === city);
        if (selectedCity?.stateCode !== nextStateCode) {
            setCity("");
        }
    };

    const handleSelectAllCities = useCallback(() => {
        setCity("");
        setPageNumber(1);
    }, []);

    const resetAllFilters = useCallback(() => {
        setQuery("");
        setSearchQuery("");
        setServiceType(DEFAULT_SERVICE_TYPE);
        setState(DEFAULT_STATE);
        setCity("");
        setPageNumber(1);
    }, []);

    const visible = listings;
    const showVerified = !DIRECTORY_IS_SAMPLE_DATA;
    const showingFrom = totalRecords === 0 ? 0 : (activePage - 1) * PAGE_SIZE + 1;
    const showingTo = Math.min(activePage * PAGE_SIZE, totalRecords);
    const showResultsArea = filtersReady && !error;
    const filtersDisabled = isLoading || !filtersReady;

    const statCards = [
        {
            label: "Listings",
            value: !filtersReady ? "…" : isLoading ? "…" : `${totalRecords.toLocaleString()}`,
            body: "Results from the national treatment directory.",
        },
        {
            label: "Search privately",
            value: "No login",
            body: "No account required to browse the directory.",
        },
        {
            label: "Filter fast",
            value: "City + state",
            body: "Find substance use, mental health, and combined care options.",
        },
    ];

    return (
        <section
            id="directory"
            ref={ref}
            data-testid="directory-section"
            className={`py-20 md:py-24 scroll-mt-24 ${isTeaser ? "bg-background" : "bg-background"}`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {DIRECTORY_IS_SAMPLE_DATA && (
                    <div
                        data-testid="directory-sample-banner"
                        className="mb-8 reveal rounded-2xl border border-amber-300/70 bg-amber-50 px-5 py-4 text-sm text-amber-950 leading-relaxed"
                    >
                        <strong>Sample listings</strong> — full directory launching soon.{" "}
                        <Link
                            to="/contact"
                            className="font-semibold underline underline-offset-2 hover:text-amber-900"
                        >
                            Sign up for launch updates →
                        </Link>
                    </div>
                )}

                <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-end mb-10 md:mb-12 reveal">
                    <div>
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                            <Compass className="w-4 h-4" />
                            Treatment Directory
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                            Private, judgment-free support that connects to real-world care.
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            Browse sober living homes, rehabs, outpatient programs, and community
                            resources by location and program type. A natural next step from Hope:
                            private support first, real-world resources whenever you&apos;re ready.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                        {statCards.map((item) => (
                            <div key={item.label} className="rounded-3xl border bg-card p-5">
                                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">
                                    {item.label}
                                </div>
                                <div className="text-2xl font-bold text-foreground mt-2">{item.value}</div>
                                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                    {item.body}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    data-testid="directory-filters"
                    className="reveal bg-card border rounded-[2rem] p-4 md:p-5 mb-8 shadow-sm"
                >
                    <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b">
                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                            <SlidersHorizontal className="w-4 h-4 text-primary" />
                            Filter resources
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {!filtersReady ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                                    Loading filters...
                                </span>
                            ) : isLoading ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                                    Loading results...
                                </span>
                            ) : totalRecords === 0 ? (
                                "No records found"
                            ) : (
                                <>
                                    Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of{" "}
                                    {totalRecords.toLocaleString()}
                                    <span className="hidden md:inline text-muted-foreground/80">
                                        {" "}
                                        · {city ? `${city}, ${state}` : state}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="ml-auto inline-flex items-center gap-2 text-xs text-muted-foreground rounded-full bg-muted px-3 py-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                            Searches stay private
                        </div>
                    </div>

                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-12 lg:col-span-5 flex h-12 items-center gap-2">
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                                placeholder="Search by name, city, program, or keyword..."
                                data-testid="directory-search-input"
                                disabled={filtersDisabled}
                                className="min-w-0 flex-1 h-full rounded-2xl bg-muted/60 border border-transparent focus:border-primary focus:bg-background px-4 outline-none text-sm text-foreground placeholder:text-muted-foreground disabled:opacity-60 transition-colors"
                            />
                            <button
                                type="button"
                                onClick={handleSearch}
                                disabled={filtersDisabled}
                                data-testid="directory-search-button"
                                aria-label="Search directory"
                                className="inline-flex h-12 shrink-0 items-center gap-2 rounded-2xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Search className="w-4 h-4" strokeWidth={2.25} aria-hidden />
                                Search
                            </button>
                        </div>
                        <div className="md:col-span-12 lg:col-span-7 flex flex-wrap gap-1.5">
                            {serviceTypeOptions.map((option) => (
                                <button
                                    key={option.key}
                                    onClick={() => setServiceType(option.key)}
                                    disabled={filtersDisabled}
                                    data-testid={`filter-type-${option.key}`}
                                    className={`px-3.5 h-9 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                        serviceType === option.key
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-muted text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                        <div className="md:col-span-12">
                            <LocationFilters
                                state={state}
                                city={city}
                                rehabStates={rehabStates}
                                availableCities={availableCities}
                                disabled={filtersDisabled}
                                onStateChange={handleStateChange}
                                onCityChange={handleCityChange}
                                onSelectAllCities={handleSelectAllCities}
                            />
                        </div>
                    </div>
                </div>

                {(error || metadataError) && (
                    <div
                        data-testid="directory-error-state"
                        className="reveal mb-6 rounded-2xl border border-rose-300/70 bg-rose-50 px-5 py-4 text-sm text-rose-950"
                    >
                        {error || metadataError}
                    </div>
                )}

                <div id={RESULTS_ANCHOR_ID} className="scroll-mt-28">
                    {isMetadataLoading ? (
                        <DirectoryLoadingGrid count={PAGE_SIZE} />
                    ) : !filtersReady ? (
                        <DirectoryEmptyState
                            title="Unable to load filters"
                            description={
                                metadataError ||
                                "Directory filters could not be loaded. Please refresh and try again."
                            }
                        />
                    ) : error ? null : isLoading ? (
                        <DirectoryLoadingGrid count={PAGE_SIZE} />
                    ) : visible.length === 0 ? (
                        <DirectoryEmptyState
                            title="No records found"
                            description="We couldn't find any treatment centers for your current filters. Try another state, program type, or search term."
                            action={
                                <button
                                    type="button"
                                    onClick={resetAllFilters}
                                    className="mt-6 inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                                >
                                    Clear filters
                                </button>
                            }
                        />
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {visible.map((l) => (
                                <DirectoryCard
                                    key={l.id}
                                    listing={l}
                                    onViewDetails={setDetailListing}
                                    showVerified={showVerified}
                                />
                            ))}
                        </div>
                    )}

                    {!isTeaser && showResultsArea && totalRecords > 0 && (
                        <DirectoryPagination
                            pageNumber={pageNumber}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>

                {detailListing && (
                    <div
                        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="directory-detail-title"
                    >
                        <button
                            type="button"
                            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={() => setDetailListing(null)}
                            aria-label="Close details"
                        />
                        <div className="relative z-10 w-full sm:max-w-2xl max-h-[min(100vh,46rem)] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-card border shadow-2xl">
                            <div className="relative aspect-[16/9] sm:aspect-[2/1] w-full overflow-hidden bg-muted">
                                <ListingImage
                                    src={detailListing.image}
                                    alt={detailListing.name}
                                    className="w-full h-full object-cover"
                                />
                                {showVerified && detailListing.verified && (
                                    <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-background/95 backdrop-blur rounded px-2 py-1 text-xs font-semibold text-primary border">
                                        <BadgeCheck size={12} />
                                        Verified
                                    </div>
                                )}
                            </div>
                            <div className="p-6 sm:p-8">
                                {DIRECTORY_IS_SAMPLE_DATA && (
                                    <p className="mb-4 rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                                        <strong>Sample listing.</strong> This facility is shown for
                                        preview only and has not been verified.
                                    </p>
                                )}
                                <div className="flex items-start justify-between gap-4">
                                    <h2
                                        id="directory-detail-title"
                                        className="text-2xl font-bold text-foreground leading-tight"
                                    >
                                        {detailListing.name}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => setDetailListing(null)}
                                        className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-border hover:bg-muted transition-colors"
                                        aria-label="Close"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                <p className="mt-1 text-sm font-medium text-primary">
                                    {getTypeLabel(detailListing.type)}
                                </p>
                                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin size={14} /> {detailListing.city}, {detailListing.state}
                                    </span>
                                    {detailListing.beds != null && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Bed size={14} /> {detailListing.beds} beds
                                        </span>
                                    )}
                                </div>
                                <p className="mt-4 text-foreground/90 leading-relaxed">
                                    {detailListing.description}
                                </p>
                                {(detailListing.phone || detailListing.website) && (
                                    <div className="mt-4 flex flex-wrap gap-3 text-sm">
                                        {detailListing.phone && (
                                            <a
                                                href={`tel:${detailListing.phone.replace(/[^\d+]/g, "")}`}
                                                className="font-semibold text-primary hover:underline"
                                            >
                                                {detailListing.phone}
                                            </a>
                                        )}
                                        {detailListing.website && (
                                            <a
                                                href={detailListing.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-semibold text-primary hover:underline"
                                            >
                                                Visit website
                                            </a>
                                        )}
                                    </div>
                                )}
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {detailListing.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground font-medium"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-6 text-xs text-muted-foreground border-t pt-4">
                                    Informational only. Genesis Recovery AI does not endorse
                                    specific providers. Verify licensing and fit before choosing care.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {mode === "teaser" && (
                    <div className="text-center mt-12 reveal">
                        <Link
                            to="/directory"
                            data-testid="browse-full-directory-button"
                            className="inline-flex items-center gap-1.5 h-12 px-6 rounded-full bg-foreground text-background hover:bg-foreground/90 text-base font-semibold transition-colors"
                        >
                            Browse full directory
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <p className="mt-4 text-xs text-muted-foreground">
                            Treatment providers shown are informational only and are not endorsements.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
